import {
  EarningsRange,
  GameHoldOutcomeFilter,
  HoldId,
  LedgerReportRange,
  OperationId,
} from "@pax/api-contract/accounting/v1alpha1";
import { GameId, PresetId, UserId } from "@pax/api-contract/common";
import { createFileRoute } from "@tanstack/react-router";
import type { SpendBy } from "@/features/ledger/balances";
import { periodsBackFrom } from "@/features/ledger/charts/PeriodStepper";
import { PAGE_SIZES } from "@/features/ledger/components";
import { instantFrom } from "@/features/ledger/format";
import {
  LEDGER_TABS,
  type LedgerLocation,
  type LedgerTab,
  LedgerWorkspace,
} from "@/features/ledger/LedgerWorkspace";
import { historyStart, historyStartValue } from "@/features/ledger/queries";
import { type DayRange, GRAPH_RANGES } from "@/features/ledger/TimeGraph";

/** One value or many, as a list of text; an empty list is dropped. */
function texts(value: unknown) {
  const list = (Array.isArray(value) ? value : [value]).filter(
    (item): item is string => typeof item === "string" && item !== "",
  );
  return list.length > 0 ? list : undefined;
}

const flag = (value: unknown) => (value === true || value === "true" ? true : undefined);

const DAY_RANGES = GRAPH_RANGES.filter((range): range is DayRange => range !== "loaded");
const SPEND_BY: readonly SpendBy[] = ["game", "player", "bucket"];

/** A zoom window, kept only whole and running forward. */
function zoomWindow(from: unknown, to: unknown) {
  const zoomFrom = instantFrom(from);
  const zoomTo = instantFrom(to);
  return zoomFrom && zoomTo && zoomFrom < zoomTo ? { zoomFrom, zoomTo } : {};
}

/** Where the tab's page starts, when the tab pages and the value is one of its kind. */
function pageStart(tab: LedgerTab, value: unknown) {
  if (tab === "player") {
    const start = historyStart(value);
    return start && historyStartValue(start);
  }
  if (tab === "game") return HoldId.safeParse(value).data;
  if (tab === "support") return OperationId.safeParse(value).data;
  return undefined;
}

export const Route = createFileRoute("/_authenticated/ledger")({
  staticData: { access: { anyOf: ["credits.view"] } },
  validateSearch: (search: Record<string, unknown>): LedgerLocation => {
    const userId = UserId.safeParse(search.userId).data;
    const tab = LEDGER_TABS.includes(search.tab as LedgerTab)
      ? (search.tab as LedgerTab)
      : undefined;
    const shownTab = tab ?? "player";
    return {
      tab,
      range: LedgerReportRange.safeParse(search.range).data,
      back: periodsBackFrom(search.back),
      accountCode: texts(search.accountCode),
      journal: texts(search.journal),
      signed: flag(search.signed),
      recipientUserId: UserId.safeParse(search.recipientUserId).data,
      granterUserId: UserId.safeParse(search.granterUserId).data,
      presetId: PresetId.safeParse(search.presetId).data,
      earningsRange: EarningsRange.safeParse(search.earningsRange).data,
      earningsBack: periodsBackFrom(search.earningsBack),
      earningsPart: texts(search.earningsPart),
      userId,
      start: pageStart(shownTab, search.start),
      gameId: GameId.safeParse(search.gameId).data,
      pageSize: PAGE_SIZES.find(size => String(size) === String(search.pageSize)),
      outcome: GameHoldOutcomeFilter.safeParse(search.outcome).data,
      placedFrom: instantFrom(search.placedFrom),
      placedTo: instantFrom(search.placedTo),
      payerUserId: UserId.safeParse(search.payerUserId).data,
      expandAll: flag(search.expandAll),
      ago: flag(search.ago),
      graph: flag(search.graph),
      graphRange: DAY_RANGES.find(range => range === search.graphRange),
      graphBack: periodsBackFrom(search.graphBack),
      graphSpend: flag(search.graphSpend),
      spendBy: SPEND_BY.find(by => by === search.spendBy),
      graphFromZero: flag(search.graphFromZero),
      graphFit: flag(search.graphFit),
      ...zoomWindow(search.zoomFrom, search.zoomTo),
    };
  },
  head: () => ({ meta: [{ title: "Ledger · Pax.co" }] }),
  component: Page,
});
function Page() {
  const location = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <LedgerWorkspace
      location={location}
      navigate={(search, history) =>
        void navigate({ search, replace: history === "replace", resetScroll: false })
      }
    />
  );
}
